import { useRef } from 'react';
import type { ReactNode } from 'react';
import { Card, Col, Dropdown, ProgressBar, Row } from 'react-bootstrap';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import healthScoreColorAndLabel from '@/helpers/healthScoreColorAndLabel';
import CrawlStatusRenderer from '@/app/common/components/CellRenderers/CrawlStatusRenderer';

interface CrawlStatus {
  completed: boolean;
  status?: number | null;
}

interface SiteHealthCardProps {
  data: number;
  title: ReactNode;
  tooltip: string;
  crawlStatus: CrawlStatus;
}

// کارت نمایش میزان سلامت سایت در نمای کلی
const SiteHealthCard = ({
  data,
  title,
  tooltip,
  crawlStatus
}: SiteHealthCardProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  return (
    <Card ref={cardRef} className="h-md-100">
      <FalconCardHeader
        title={
          <div>
            {title} <HelpTooltip html={tooltip} />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={
          <Dropdown
            align="end"
            className="btn-reveal-trigger d-inline-block me-0"
          >
            <Dropdown.Toggle
              className="text-600 btn-reveal"
              split
              variant="reveal"
              size="sm"
            >
              <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
              <Dropdown.Item
                onClick={() => {
                  cardRef.current?.requestFullscreen();
                }}
                className="link-600 fs--1 d-none d-md-flex align-items-center gap-1 py-1"
              >
                تمام صفحه
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        }
      />
      <Card.Body className="py-3">
        <Row className="justify-content-between g-0">
          <Col xs={5} sm={6} xxl className="pe-2 d-flex align-items-center">
            {crawlStatus.completed ? (
              <div
                className={`ps-0 fw-black display-3`}
                style={{ color: healthScoreColorAndLabel(data).color }}
              >
                {data}%
              </div>
            ) : (
              <CrawlStatusRenderer status={crawlStatus.status} />
            )}
          </Col>
          <Col xs="auto">
            {/* نمایش حرف میزان سلامت سایت و رنگ آن */}
            <div
              className={`ps-0 fw-black display-1`}
              style={{ color: healthScoreColorAndLabel(data).color }}
            >
              {crawlStatus.completed ? (
                healthScoreColorAndLabel(data).label
              ) : (
                <span>?</span>
              )}
            </div>
          </Col>
          <div className="mt-2">
            {/* نوار سلامت */}
            <ProgressBar
              variant={healthScoreColorAndLabel(data).prClass}
              now={data}
              label={data + '%'}
            />
            <style>
              {/* تنظیم کنندگان رنگ نوار سلامت در کارت */}
              {`.bg-health-score-success {
              background-color:${healthScoreColorAndLabel(95).color} !important;
            }`}
              {`.bg-health-score-yellow {
              background-color:${healthScoreColorAndLabel(85).color} !important;
            }`}
              {`.bg-health-score-warning {
              background-color:${healthScoreColorAndLabel(75).color} !important;
            }`}
              {`.bg-health-score-danger {
              background-color:${healthScoreColorAndLabel(55).color} !important;
            }`}
            </style>
          </div>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default SiteHealthCard;
